import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { getWelcomeText, getWelcomeList } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';

/**
 * Tela 10 — Tudo Pronto! 🎉
 * Resumo celebratório com blocos:
 * - Header temático
 * - Checklist de tudo que foi criado
 * - Links (Copiar/Abrir)
 * - Próximos passos
 * - Botão "Entrar no Agendário"
 */
export function Step10Summary({ wizard, goNext }) {
  wlog('Step10Summary', 'RENDER', { hasResult: !!wizard.state?.result });
  const template = wizard.template;
  const result = wizard.state.result;
  const firstName = wizard.state.userName.split(' ')[0].trim();
  const businessNoun = template?.welcome?.businessNoun || 'negócio';

  // Textos carregados assincronamente
  const [summaryTitle, setSummaryTitle] = useState('');
  const [summarySubtitle, setSummarySubtitle] = useState('');
  const [placePageText, setPlacePageText] = useState('');
  const [professionalPageText, setProfessionalPageText] = useState('');
  const [bioPageText, setBioPageText] = useState('');
  const [summarySteps, setSummarySteps] = useState([]);
  const [exitButtonLabel, setExitButtonLabel] = useState('');

  // Liberar o lock do Welcome assim que o resumo é exibido.
  // Garante que qualquer link (editar, próximos passos, etc.) funcione sem reabrir o wizard.
  useEffect(() => {
    wizard.completeAndExit();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Carrega todos os textos temáticos de forma assíncrona
    (async () => {
      const title = await getWelcomeText(template, 'summaryTitle', {
        firstName,
        businessNoun,
      });
      const subtitle = await getWelcomeText(template, 'summarySubtitle', {
        firstName,
      });
      const placeText = await getWelcomeText(template, 'summaryPlacePageText', { firstName });
      const professionalText = await getWelcomeText(template, 'summaryProfessionalPageText', { firstName });
      const bioText = await getWelcomeText(template, 'summaryBioPageText', { firstName });
      const steps = await getWelcomeList(template, 'summarySteps');
      const buttonLabel = await getWelcomeText(template, 'summaryExitButtonLabel', { firstName });

      setSummaryTitle(title || `🎉 Sua ${businessNoun} está no ar, ${firstName}!`);
      setSummarySubtitle(subtitle || 'Aqui está tudo que já está funcionando:');
      setPlacePageText(placeText || 'Esse é o site do seu negócio. Personalize com fotos e descrição.');
      setProfessionalPageText(professionalText || 'Site completo do profissional, com agenda, serviços e bio. Compartilhe como um cartão de visitas digital.');
      setBioPageText(bioText || 'Cole esse link na bio do Instagram e comece a receber agendamentos hoje.');
      setSummarySteps(steps || []);
      setExitButtonLabel(buttonLabel || 'Entrar no Agendário →');
    })();
    // NÃO focar o título via querySelector: durante a animação de transform
    // do trilho, o focus() rola o slide para fora da vista → tela branca.
  }, [template, firstName, businessNoun]);

  if (!result) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h1">Carregando...</Typography>
      </Container>
    );
  }

  // Normaliza created — API retorna arrays, mas código legado usava contadores escalares
  const profCount = result.created?.professionals?.length ?? result.created?.professionalsCount ?? 0;
  const svcCount = result.created?.services?.length ?? result.created?.servicesCount ?? 0;
  const placeName = result.created?.places?.[0]?.name ?? result.created?.placeName ?? null;
  const schedCount = result.created?.schedules?.length ?? result.created?.schedulesCount ?? 0;
  const placePageCount = result.links?.placePages?.length ?? result.created?.pagesCount ?? 0;
  const professionalPageCount = result.links?.professionalPages?.length ?? 0;
  const bioPageCount = result.links?.bioPages?.length ?? 0;
  const appointmentCreated = result.created?.appointmentCreated ?? false;

  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`Copiado: ${label}`);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const handleShare = async (url, title) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Erro ao compartilhar:', error);
      }
    } else {
      // Fallback: copiar para a área de transferência
      await handleCopyToClipboard(url, title);
    }
  };

  const handleExit = () => {
    wizard.completeAndExit();
    window.location.href = '/dashboard';
  };

  const handleNextStepLink = (link) => {
    wizard.completeAndExit();
    window.location.href = link;
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100%',
        py: 4,
      }}
      data-step="summary"
    >
      <Box component="section" sx={{ width: '100%' }}>
        {/* Header Celebratório - Enhanced Visual Hierarchy */}
        <Box
          sx={{
            mb: 4,
            p: 3,
            bgcolor: 'rgba(76, 175, 80, 0.08)',
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'success.light',
            textAlign: 'center',
          }}
        >
          {/* Emoji grande de celebração */}
          <Typography
            sx={{
              fontSize: { xs: '48px', md: '64px' },
              mb: 2,
              lineHeight: 1,
            }}
          >
            🎉
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '28px', md: '36px' },
              fontWeight: 'bold',
              mb: 1,
              color: 'success.main',
              backgroundClip: 'text',
            }}
            tabIndex={-1}
          >
            {summaryTitle}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '14px', md: '16px' },
              fontWeight: 500,
            }}
          >
            {summarySubtitle}
          </Typography>
        </Box>

        {/* Bloco 1: O que já está pronto */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '18px', md: '20px' },
              fontWeight: 'bold',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            ✅ O que já está pronto
          </Typography>

          <List
            sx={{
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <>
              {profCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${profCount} profissional${profCount > 1 ? 's' : ''} cadastrado${profCount > 1 ? 's' : ''}`}
                  />
                </ListItem>
              )}

              {svcCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${svcCount} serviço${svcCount > 1 ? 's' : ''} com preço e duração`}
                  />
                </ListItem>
              )}

              {placeName && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={`Local "${placeName}" criado`} />
                </ListItem>
              )}

              {schedCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${schedCount} agenda${schedCount > 1 ? 's' : ''} ativa${schedCount > 1 ? 's' : ''}`}
                  />
                </ListItem>
              )}

              {placePageCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Página do local criada" />
                </ListItem>
              )}

              {professionalPageCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${professionalPageCount} site${professionalPageCount > 1 ? 's' : ''} profissional${professionalPageCount > 1 ? 'is' : ''} criado${professionalPageCount > 1 ? 's' : ''}`}
                  />
                </ListItem>
              )}

              {bioPageCount > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Link in bio criado${bioPageCount > 1 ? ` (${bioPageCount} profissionais)` : ''}`}
                  />
                </ListItem>
              )}

              {appointmentCreated && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Primeiro agendamento registrado 💜" />
                </ListItem>
              )}
            </>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Bloco 2: Seus Links */}
        {result.links && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{ fontSize: '18px', fontWeight: 'bold', mb: 2 }}
            >
              🔗 Seus Links
            </Typography>

            <Stack spacing={2}>
              {/* Páginas de local */}
              {result.links.placePages &&
                result.links.placePages.map((page) => (
                  <Card key={page.id}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        📄 Página do Local
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {placePageText}
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Copiar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(page.url, '_blank')}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Abrir
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => handleShare(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Compartilhar
                        </Button>
                        {page.editUrl && (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => { wizard.completeAndExit(); window.location.href = page.editUrl; }}
                            fullWidth={{ xs: true, sm: false }}
                          >
                            Editar
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

              {/* Site profissional completo por profissional */}
              {result.links.professionalPages &&
                result.links.professionalPages.map((page) => (
                  <Card key={page.id}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        👤 Site Profissional — {page.professionalName || page.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {professionalPageText}
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Copiar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(page.url, '_blank')}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Abrir
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => handleShare(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Compartilhar
                        </Button>
                        {page.editUrl && (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => { wizard.completeAndExit(); window.location.href = page.editUrl; }}
                            fullWidth={{ xs: true, sm: false }}
                          >
                            Editar
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

              {/* Link in Bio por profissional */}
              {result.links.bioPages &&
                result.links.bioPages.map((page) => (
                  <Card key={page.id}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        🔗 Link in Bio — {page.professionalName || page.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {bioPageText}
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Copiar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(page.url, '_blank')}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Abrir
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => handleShare(page.url, page.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Compartilhar
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

              {/* Links de agendamento direto */}
              {result.links.bookingLinks &&
                result.links.bookingLinks.map((link) => (
                  <Card key={link.id}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        📅 {link.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Link direto para agendamento.
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(link.url, link.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Copiar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(link.url, '_blank')}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Abrir
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => handleShare(link.url, link.name)}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Compartilhar
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Bloco 3: Próximos Passos */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{ fontSize: '18px', fontWeight: 'bold', mb: 2 }}
          >
            🎯 Próximos Passos
          </Typography>

          <Stack spacing={2}>
            {summarySteps?.map((step, index) => (
              <Card key={index}>
                <CardContent>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {step.emoji} {step.text}
                  </Typography>
                  {step.link && (
                    <Link
                      component="button"
                      onClick={() => handleNextStepLink(step.link)}
                      sx={{ display: 'inline-block', mt: 1, fontSize: '0.875rem' }}
                    >
                      Acessar →
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* Botão Principal */}
        <Button
          variant="contained"
          size="large"
          onClick={handleExit}
          sx={{
            width: '100%',
            py: 2,
            fontSize: '16px',
            fontWeight: 600,
          }}
        >
          {exitButtonLabel}
        </Button>
      </Box>
    </Container>
  );
}

export default Step10Summary;
